import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Skeleton,
  Fade,
  Paper,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import {
  Users,
  UserPlus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Shield,
  UserCheck,
  UserX,
  Mail,
  Phone,
  MapPin,
  Building,
  Clock,
  TrendingUp,
  Eye,
  Settings,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../components/common/Notification';
import { authService } from '../../services/auth';
import { statisticsService } from '../../services/statistics';
import CustomButton from '../../components/ui/CustomButton';

const UserCard = ({ user, onEdit, onToggleStatus, onView, isDarkMode }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotification();

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleStatus = async () => {
    setIsLoading(true);
    try {
      await onToggleStatus(user);
      showNotification(
        `User ${user.IsActive ? 'deactivated' : 'activated'} successfully`,
        'success'
      );
    } catch (error) {
      showNotification('Failed to update user status', 'error');
    } finally {
      setIsLoading(false);
      handleMenuClose();
    }
  };

  const getRoleColor = roles => {
    if (roles?.includes('Admin')) return '#3b82f6';
    return '#10b981';
  };

  const getRoleIcon = roles => {
    if (roles?.includes('Admin')) return <Shield size={14} />;
    return <Building size={14} />;
  };

  return (
    <Fade in={true} timeout={300}>
      <Card
        sx={{
          height: '100%',
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: 3,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: isDarkMode
              ? '0 20px 40px rgba(0, 0, 0, 0.4)'
              : '0 20px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header with Avatar and Actions */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                mr: 2,
                background: `linear-gradient(135deg, ${getRoleColor(user.Roles)}, ${getRoleColor(user.Roles)}CC)`,
                fontSize: '1.25rem',
                fontWeight: 600,
                boxShadow: `0 4px 12px ${getRoleColor(user.Roles)}40`,
              }}
            >
              {user.FirstName?.charAt(0)}
              {user.LastName?.charAt(0)}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  mb: 0.5,
                }}
              >
                {user.FirstName} {user.LastName}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {getRoleIcon(user.Roles)}
                <Chip
                  label={user.Roles?.[0] || 'Office'}
                  size="small"
                  sx={{
                    ml: 0.5,
                    height: 20,
                    fontSize: '0.7rem',
                    background: `linear-gradient(135deg, ${getRoleColor(user.Roles)}20, ${getRoleColor(user.Roles)}10)`,
                    color: getRoleColor(user.Roles),
                    border: `1px solid ${getRoleColor(user.Roles)}30`,
                  }}
                />
              </Box>

              <Chip
                label={user.IsActive ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  background: user.IsActive
                    ? 'linear-gradient(135deg, #10b98120, #10b98110)'
                    : 'linear-gradient(135deg, #ef444420, #ef444410)',
                  color: user.IsActive ? '#10b981' : '#ef4444',
                  border: user.IsActive
                    ? '1px solid #10b98130'
                    : '1px solid #ef444430',
                }}
              />
            </Box>

            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                '&:hover': {
                  background: isDarkMode
                    ? 'rgba(148, 163, 184, 0.1)'
                    : 'rgba(148, 163, 184, 0.1)',
                },
              }}
              disabled={isLoading}
            >
              <MoreVertical size={16} />
            </IconButton>
          </Box>

          {/* User Details */}
          <Box sx={{ mt: 2, space: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Mail size={14} color={isDarkMode ? '#94a3b8' : '#64748b'} />
              <Typography
                variant="body2"
                sx={{
                  ml: 1,
                  color: isDarkMode ? '#cbd5e1' : '#475569',
                  fontSize: '0.8rem',
                }}
              >
                {user.Email}
              </Typography>
            </Box>

            {user.PhoneNumber && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Phone size={14} color={isDarkMode ? '#94a3b8' : '#64748b'} />
                <Typography
                  variant="body2"
                  sx={{
                    ml: 1,
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  {user.PhoneNumber}
                </Typography>
              </Box>
            )}

            {user.OfficeLocation && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MapPin size={14} color={isDarkMode ? '#94a3b8' : '#64748b'} />
                <Typography
                  variant="body2"
                  sx={{
                    ml: 1,
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  {user.OfficeLocation}
                </Typography>
              </Box>
            )}

            {user.Department && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Building
                  size={14}
                  color={isDarkMode ? '#94a3b8' : '#64748b'}
                />
                <Typography
                  variant="body2"
                  sx={{
                    ml: 1,
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  {user.Department}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Clock size={12} color={isDarkMode ? '#64748b' : '#94a3b8'} />
              <Typography
                variant="caption"
                sx={{
                  ml: 0.5,
                  color: isDarkMode ? '#64748b' : '#94a3b8',
                  fontSize: '0.7rem',
                }}
              >
                Created: {new Date(user.CreatedAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>

          {isLoading && (
            <LinearProgress
              sx={{
                mt: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                },
              }}
            />
          )}
        </CardContent>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              borderRadius: 2,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              onView(user);
              handleMenuClose();
            }}
          >
            <Eye size={16} style={{ marginRight: 8 }} />
            View Details
          </MenuItem>
          <MenuItem
            onClick={() => {
              onEdit(user);
              handleMenuClose();
            }}
          >
            <Edit size={16} style={{ marginRight: 8 }} />
            Edit User
          </MenuItem>
          <MenuItem onClick={handleToggleStatus}>
            {user.IsActive ? (
              <>
                <UserX size={16} style={{ marginRight: 8 }} />
                Deactivate
              </>
            ) : (
              <>
                <UserCheck size={16} style={{ marginRight: 8 }} />
                Activate
              </>
            )}
          </MenuItem>
        </Menu>
      </Card>
    </Fade>
  );
};

const ViewUserDialog = ({ open, onClose, user, isDarkMode }) => {
  if (!user) return null;

  const formatDate = dateString => {
    return new Date(dateString).toLocaleString();
  };

  const getRoleColor = roles => {
    if (roles?.includes('Admin')) return '#3b82f6';
    return '#10b981';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Eye size={24} color="#06b6d4" />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
            }}
          >
            User Details
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* User Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                background: `linear-gradient(135deg, ${getRoleColor(user.Roles)}, ${getRoleColor(user.Roles)}CC)`,
                fontSize: '2rem',
                fontWeight: 600,
                boxShadow: `0 8px 24px ${getRoleColor(user.Roles)}40`,
              }}
            >
              {user.FirstName?.charAt(0)}
              {user.LastName?.charAt(0)}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  mb: 1,
                }}
              >
                {user.FirstName} {user.LastName}
              </Typography>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <Chip
                  label={user.Roles?.[0] || 'Office'}
                  size="small"
                  icon={
                    user.Roles?.includes('Admin') ? (
                      <Shield size={14} />
                    ) : (
                      <Building size={14} />
                    )
                  }
                  sx={{
                    background: `linear-gradient(135deg, ${getRoleColor(user.Roles)}20, ${getRoleColor(user.Roles)}10)`,
                    color: getRoleColor(user.Roles),
                    border: `1px solid ${getRoleColor(user.Roles)}30`,
                  }}
                />

                <Chip
                  label={user.IsActive ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    background: user.IsActive
                      ? 'linear-gradient(135deg, #10b98120, #10b98110)'
                      : 'linear-gradient(135deg, #ef444420, #ef444410)',
                    color: user.IsActive ? '#10b981' : '#ef4444',
                    border: user.IsActive
                      ? '1px solid #10b98130'
                      : '1px solid #ef444430',
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* User Information Grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                  border: isDarkMode
                    ? '1px solid rgba(148, 163, 184, 0.1)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    mb: 2,
                  }}
                >
                  Contact Information
                </Typography>

                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Mail
                      size={16}
                      color={isDarkMode ? '#94a3b8' : '#64748b'}
                    />
                    <Box sx={{ ml: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                      >
                        Email
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                          fontWeight: 500,
                        }}
                      >
                        {user.Email}
                      </Typography>
                    </Box>
                  </Box>

                  {user.PhoneNumber && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Phone
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Box sx={{ ml: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                        >
                          Phone
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                            fontWeight: 500,
                          }}
                        >
                          {user.PhoneNumber}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                  border: isDarkMode
                    ? '1px solid rgba(148, 163, 184, 0.1)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    mb: 2,
                  }}
                >
                  Office Information
                </Typography>

                <Box sx={{ space: 2 }}>
                  {user.OfficeLocation && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MapPin
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Box sx={{ ml: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                        >
                          Office Location
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                            fontWeight: 500,
                          }}
                        >
                          {user.OfficeLocation}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {user.Department && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Building
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Box sx={{ ml: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                        >
                          Department
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                            fontWeight: 500,
                          }}
                        >
                          {user.Department}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {user.OfficeName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Building
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Box sx={{ ml: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                        >
                          Office Name
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                            fontWeight: 500,
                          }}
                        >
                          {user.OfficeName}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                  border: isDarkMode
                    ? '1px solid rgba(148, 163, 184, 0.1)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    mb: 2,
                  }}
                >
                  Account Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Clock
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Box sx={{ ml: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                        >
                          Created
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                            fontWeight: 500,
                          }}
                        >
                          {formatDate(user.CreatedAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {user.LastLoginAt && (
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <UserCheck
                          size={16}
                          color={isDarkMode ? '#94a3b8' : '#64748b'}
                        />
                        <Box sx={{ ml: 2 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                          >
                            Last Login
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? '#f1f5f9' : '#1e293b',
                              fontWeight: 500,
                            }}
                          >
                            {formatDate(user.LastLoginAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Shield
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Box sx={{ ml: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                        >
                          User ID
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                            fontWeight: 500,
                            fontFamily: 'monospace',
                          }}
                        >
                          {user.Id?.substring(0, 8)}...
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CustomButton variant="outlined" onClick={onClose}>
          Close
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

const EditUserDialog = ({ open, onClose, user, onUpdateUser, isDarkMode }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: 'Office',
    officeLocation: '',
    department: '',
    officeName: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.FirstName || '',
        lastName: user.LastName || '',
        email: user.Email || '',
        phoneNumber: user.PhoneNumber || '',
        role: user.Roles?.[0] || 'Office',
        officeLocation: user.OfficeLocation || '',
        department: user.Department || '',
        officeName: user.OfficeName || '',
      });
    }
  }, [user]);

  const handleInputChange = field => event => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phoneNumber)
      newErrors.phoneNumber = 'Phone number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await onUpdateUser(user.Id, formData);
      onClose();
    } catch (error) {
      console.error('Update user error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Edit size={24} color="#8b5cf6" />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
            }}
          >
            Edit User: {user.FirstName} {user.LastName}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="First Name"
              fullWidth
              value={formData.firstName}
              onChange={handleInputChange('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Last Name"
              fullWidth
              value={formData.lastName}
              onChange={handleInputChange('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              variant="outlined"
              disabled // Email typically shouldn't be editable
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Phone Number"
              fullWidth
              value={formData.phoneNumber}
              onChange={handleInputChange('phoneNumber')}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={handleInputChange('role')}
                label="Role"
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Office">Office</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Office Location"
              fullWidth
              value={formData.officeLocation}
              onChange={handleInputChange('officeLocation')}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Department"
              fullWidth
              value={formData.department}
              onChange={handleInputChange('department')}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Office Name"
              fullWidth
              value={formData.officeName}
              onChange={handleInputChange('officeName')}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CustomButton variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="gradient"
          gradient="secondary"
          onClick={handleSubmit}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Edit size={16} />
            )
          }
        >
          {isLoading ? 'Updating...' : 'Update User'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

const ChangePasswordDialog = ({ open, onClose, isDarkMode }) => {
  const { token } = useAuth();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = field => event => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword)
      newErrors.currentPassword = 'Current password is required';
    if (!formData.newPassword)
      newErrors.newPassword = 'New password is required';
    if (formData.newPassword.length < 6)
      newErrors.newPassword = 'Password must be at least 6 characters';
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await authService.changePassword(
        formData.currentPassword,
        formData.newPassword,
        token
      );
      showNotification('Password changed successfully', 'success');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      onClose();
    } catch (error) {
      showNotification('Failed to change password', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Settings size={24} color="#f59e0b" />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
            }}
          >
            Change Password
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Current Password"
              type="password"
              fullWidth
              value={formData.currentPassword}
              onChange={handleInputChange('currentPassword')}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={formData.newPassword}
              onChange={handleInputChange('newPassword')}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CustomButton variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="gradient"
          gradient="warning"
          onClick={handleSubmit}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Settings size={16} />
            )
          }
        >
          {isLoading ? 'Changing...' : 'Change Password'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

const UsersActivityLog = ({ isDarkMode }) => {
  const { token } = useAuth();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setIsLoading(true);
        // Use the same service as dashboard
        const activitiesData = await statisticsService.getRecentActivities();

        // Filter to show only user-related activities and add some mock user activities
        const userActivities = activitiesData.filter(
          activity =>
            activity.type === 'user' ||
            activity.title.toLowerCase().includes('user')
        );

        // Add some additional user-specific activities for demonstration
        const additionalUserActivities = [
          {
            id: `user-activity-${Date.now()}`,
            title: 'User login activity detected',
            description: 'Multiple users logged in during peak hours',
            time: 'Just now',
            type: 'user_login',
            timestamp: new Date(),
          },
          {
            id: `user-activity-${Date.now() + 1}`,
            title: 'Password security check',
            description: 'System performed routine password security audit',
            time: '5 minutes ago',
            type: 'password_changed',
            timestamp: new Date(Date.now() - 5 * 60 * 1000),
          },
        ];

        // Combine real activities with additional user activities
        const combinedActivities = [
          ...userActivities,
          ...additionalUserActivities,
        ]
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, 8); // Show latest 8 activities

        setActivities(combinedActivities);
      } catch (error) {
        console.error('Error fetching user activities:', error);
        // Fallback to empty array if API fails
        setActivities([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivities();
  }, [token]);

  const getActivityIcon = type => {
    switch (type) {
      case 'create':
      case 'user':
        return <UserPlus size={16} />;
      case 'user_login':
        return <UserCheck size={16} />;
      case 'user_updated':
        return <Edit size={16} />;
      case 'user_deactivated':
        return <UserX size={16} />;
      case 'password_changed':
        return <Settings size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getActivityColor = type => {
    switch (type) {
      case 'create':
      case 'user':
        return '#10b981';
      case 'user_login':
        return '#06b6d4';
      case 'user_updated':
        return '#8b5cf6';
      case 'user_deactivated':
        return '#ef4444';
      case 'password_changed':
        return '#f59e0b';
      default:
        return '#64748b';
    }
  };

  const formatTimeAgo = timestamp => {
    if (!timestamp) return 'Unknown time';

    const now = new Date();
    const date = new Date(timestamp);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
        backdropFilter: 'blur(20px)',
        border: isDarkMode
          ? '1px solid rgba(148, 163, 184, 0.1)'
          : '1px solid rgba(148, 163, 184, 0.2)',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(0, 0, 0, 0.3)'
          : '0 8px 32px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Clock size={24} color="#06b6d4" />
        <Typography
          variant="h6"
          sx={{
            ml: 1,
            fontWeight: 600,
            color: isDarkMode ? '#f1f5f9' : '#1e293b',
          }}
        >
          Recent User Activities
        </Typography>
      </Box>

      {isLoading ? (
        <Box>
          {Array(5)
            .fill({})
            .map((_, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ mr: 2 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="70%" height={20} />
                  <Skeleton variant="text" width="50%" height={16} />
                </Box>
                <Skeleton variant="text" width="60px" height={16} />
              </Box>
            ))}
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {activities.map((activity, index) => (
            <Fade in={true} timeout={300 + index * 100} key={activity.id}>
              <ListItem
                sx={{
                  px: 0,
                  py: 1.5,
                  borderBottom:
                    index < activities.length - 1
                      ? isDarkMode
                        ? '1px solid rgba(148, 163, 184, 0.1)'
                        : '1px solid rgba(148, 163, 184, 0.2)'
                      : 'none',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: 2,
                  '&:hover': {
                    background: isDarkMode
                      ? 'rgba(59, 130, 246, 0.05)'
                      : 'rgba(59, 130, 246, 0.02)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      background: `linear-gradient(135deg, ${getActivityColor(activity.type)}, ${getActivityColor(activity.type)}CC)`,
                      width: 40,
                      height: 40,
                      boxShadow: `0 4px 12px ${getActivityColor(activity.type)}30`,
                    }}
                  >
                    {getActivityIcon(activity.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: isDarkMode ? '#f1f5f9' : '#1e293b',
                      }}
                    >
                      {activity.title}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                        }}
                        component="div"
                      >
                        {activity.description}
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ textAlign: 'right' }}>
                  <Chip
                    label={activity.time || formatTimeAgo(activity.timestamp)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      background: `linear-gradient(135deg, ${getActivityColor(activity.type)}20, ${getActivityColor(activity.type)}10)`,
                      color: getActivityColor(activity.type),
                      border: `1px solid ${getActivityColor(activity.type)}30`,
                    }}
                  />
                </Box>
              </ListItem>
            </Fade>
          ))}
        </List>
      )}
    </Paper>
  );
};

const CreateUserDialog = ({ open, onClose, onCreateUser, isDarkMode }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: 'Office',
    officeLocation: '',
    department: '',
    officeName: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = field => event => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.phoneNumber)
      newErrors.phoneNumber = 'Phone number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await onCreateUser(formData);
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        role: 'Office',
        officeLocation: '',
        department: '',
        officeName: '',
      });
      onClose();
    } catch (error) {
      console.error('Create user error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <UserPlus size={24} color="#3b82f6" />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
            }}
          >
            Create New User
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="First Name"
              fullWidth
              value={formData.firstName}
              onChange={handleInputChange('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Last Name"
              fullWidth
              value={formData.lastName}
              onChange={handleInputChange('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={formData.password}
              onChange={handleInputChange('password')}
              error={!!errors.password}
              helperText={errors.password}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Phone Number"
              fullWidth
              value={formData.phoneNumber}
              onChange={handleInputChange('phoneNumber')}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={handleInputChange('role')}
                label="Role"
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Office">Office</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Office Location"
              fullWidth
              value={formData.officeLocation}
              onChange={handleInputChange('officeLocation')}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Department"
              fullWidth
              value={formData.department}
              onChange={handleInputChange('department')}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Office Name"
              fullWidth
              value={formData.officeName}
              onChange={handleInputChange('officeName')}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CustomButton variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="gradient"
          gradient="primary"
          onClick={handleSubmit}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <UserPlus size={16} />
            )
          }
        >
          {isLoading ? 'Creating...' : 'Create User'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

const UsersPage = () => {
  const { user, isAdmin, token } = useAuth();
  const { isDarkMode } = useTheme();
  const { showNotification, NotificationComponent } = useNotification();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    admins: 0,
    offices: 0,
  });

  // Fetch users data
  const fetchUsers = async () => {
    if (!isAdmin()) {
      setError('Access denied: Admin privileges required');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await authService.getUsers(token);
      setUsers(response);
      setFilteredUsers(response);

      // Calculate stats
      const totalUsers = response.length;
      const activeUsers = response.filter(u => u.IsActive !== false).length;
      const adminUsers = response.filter(u =>
        u.Roles?.includes('Admin')
      ).length;
      const officeUsers = response.filter(
        u => !u.Roles?.includes('Admin')
      ).length;

      setStats({
        total: totalUsers,
        active: activeUsers,
        admins: adminUsers,
        offices: officeUsers,
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
      showNotification('Failed to load users', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(
        user =>
          user.FirstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.LastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.Email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => {
        if (roleFilter === 'admin') return user.Roles?.includes('Admin');
        if (roleFilter === 'office') return !user.Roles?.includes('Admin');
        return true;
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => {
        if (statusFilter === 'active') return user.IsActive !== false;
        if (statusFilter === 'inactive') return user.IsActive === false;
        return true;
      });
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, statusFilter, users]);

  const handleCreateUser = async userData => {
    try {
      await authService.createUser(userData, token);
      showNotification('User created successfully', 'success');
      fetchUsers(); // Refresh the list
    } catch (error) {
      showNotification('Failed to create user', 'error');
      throw error;
    }
  };

  const handleToggleUserStatus = async userToToggle => {
    try {
      if (userToToggle.IsActive) {
        await authService.deactivateUser(userToToggle.Id, token);
      } else {
        await authService.activateUser(userToToggle.Id, token);
      }
      fetchUsers(); // Refresh the list
    } catch (error) {
      throw error;
    }
  };

  const handleEditUser = userToEdit => {
    setSelectedUser(userToEdit);
    setEditDialogOpen(true);
  };

  const handleViewUser = userToView => {
    setSelectedUser(userToView);
    setViewDialogOpen(true);
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      await authService.updateUserProfile(userId, userData, token);
      showNotification('User updated successfully', 'success');
      fetchUsers(); // Refresh the list
    } catch (error) {
      showNotification('Failed to update user', 'error');
      throw error;
    }
  };

  // Statistics cards data
  const statsCards = [
    {
      title: 'Total Users',
      value: stats.total,
      icon: Users,
      color: '#3b82f6',
      change: '+12%',
    },
    {
      title: 'Active Users',
      value: stats.active,
      icon: UserCheck,
      color: '#10b981',
      change: '+8%',
    },
    {
      title: 'Administrators',
      value: stats.admins,
      icon: Shield,
      color: '#8b5cf6',
      change: '+2%',
    },
    {
      title: 'Office Users',
      value: stats.offices,
      icon: Building,
      color: '#06b6d4',
      change: '+15%',
    },
  ];

  if (!isAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You need administrator privileges to access user management.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'transparent' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              textShadow: isDarkMode
                ? '0 0 20px rgba(59, 130, 246, 0.3)'
                : 'none',
            }}
          >
            User Management 👥
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              fontSize: '1.1rem',
            }}
          >
            Manage system users, roles, and permissions
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <CustomButton
            variant="gradient"
            gradient="warning"
            onClick={() => setPasswordDialogOpen(true)}
            startIcon={<Settings size={18} />}
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
              boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d97706 0%, #dc2626 100%)',
                boxShadow: '0 8px 25px rgba(245, 158, 11, 0.6)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Change Password
          </CustomButton>

          <CustomButton
            variant="outlined"
            onClick={fetchUsers}
            disabled={isLoading}
            startIcon={<RefreshCw size={16} />}
            sx={{
              borderColor: isDarkMode ? '#374151' : '#d1d5db',
              color: isDarkMode ? '#e5e7eb' : '#374151',
              '&:hover': {
                borderColor: '#3b82f6',
                color: '#3b82f6',
                background: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            Refresh
          </CustomButton>

          <CustomButton
            variant="gradient"
            gradient="primary"
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<UserPlus size={18} />}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Create New User
          </CustomButton>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <CustomButton
              size="small"
              onClick={fetchUsers}
              startIcon={<RefreshCw size={14} />}
            >
              Retry
            </CustomButton>
          }
        >
          <Typography variant="body2">
            <strong>Error loading users:</strong> {error}
          </Typography>
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            {isLoading ? (
              <Card
                sx={{
                  height: 120,
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: isDarkMode
                    ? '1px solid rgba(148, 163, 184, 0.1)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Skeleton variant="text" width="60%" height={32} />
                      <Skeleton variant="text" width="80%" height={20} />
                    </Box>
                    <Skeleton variant="circular" width={48} height={48} />
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Fade in={true} timeout={300 + index * 100}>
                <Card
                  sx={{
                    height: 120,
                    background: isDarkMode
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: isDarkMode
                      ? '1px solid rgba(148, 163, 184, 0.1)'
                      : '1px solid rgba(148, 163, 184, 0.2)',
                    boxShadow: isDarkMode
                      ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                      : '0 8px 32px rgba(0, 0, 0, 0.1)',
                    borderRadius: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDarkMode
                        ? `0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px ${stat.color}20`
                        : `0 20px 40px rgba(0, 0, 0, 0.15), 0 0 30px ${stat.color}20`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: stat.color,
                            mb: 0.5,
                            textShadow: `0 0 20px ${stat.color}40`,
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? '#94a3b8' : '#64748b',
                            fontWeight: 500,
                          }}
                        >
                          {stat.title}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`,
                          border: `1px solid ${stat.color}30`,
                          boxShadow: `0 8px 16px ${stat.color}30`,
                        }}
                      >
                        <stat.icon size={20} color={stat.color} />
                      </Box>
                    </Box>

                    {stat.change && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
                      >
                        <TrendingUp size={12} color="#10b981" />
                        <Typography
                          variant="caption"
                          sx={{
                            ml: 0.5,
                            color: '#10b981',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        >
                          {stat.change} from last month
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Search and Filters */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      size={20}
                      color={isDarkMode ? '#94a3b8' : '#64748b'}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.7)'
                      : 'rgba(255, 255, 255, 0.7)',
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role Filter</InputLabel>
              <Select
                value={roleFilter}
                onChange={e => setRoleFilter(e.target.value)}
                label="Role Filter"
                sx={{
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="admin">Administrators</MenuItem>
                <MenuItem value="office">Office Users</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                label="Status Filter"
                sx={{
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
          >
            Showing {filteredUsers.length} of {users.length} users
          </Typography>
        </Box>
      </Paper>

      {/* Users Grid */}
      {isLoading ? (
        <Grid container spacing={3}>
          {Array(6)
            .fill({})
            .map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: 280,
                    background: isDarkMode
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: isDarkMode
                      ? '1px solid rgba(148, 163, 184, 0.1)'
                      : '1px solid rgba(148, 163, 184, 0.2)',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Skeleton
                        variant="circular"
                        width={56}
                        height={56}
                        sx={{ mr: 2 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={24} />
                        <Skeleton variant="text" width="50%" height={20} />
                      </Box>
                    </Box>
                    {Array(4)
                      .fill({})
                      .map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="text"
                          width="90%"
                          height={20}
                          sx={{ mb: 1 }}
                        />
                      ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      ) : filteredUsers.length > 0 ? (
        <Grid container spacing={3}>
          {filteredUsers.map((userItem, index) => (
            <Grid item xs={12} sm={6} md={4} key={userItem.Id}>
              <UserCard
                user={userItem}
                onEdit={handleEditUser}
                onToggleStatus={handleToggleUserStatus}
                onView={handleViewUser}
                isDarkMode={isDarkMode}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Users
            size={64}
            color={isDarkMode ? '#64748b' : '#94a3b8'}
            style={{ marginBottom: 16 }}
          />
          <Typography
            variant="h6"
            sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mb: 1 }}
          >
            No users found
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', mb: 3 }}
          >
            {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your search filters'
              : 'Start by creating your first user'}
          </Typography>
          {!searchTerm && roleFilter === 'all' && statusFilter === 'all' && (
            <CustomButton
              variant="gradient"
              gradient="primary"
              startIcon={<UserPlus size={16} />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create First User
            </CustomButton>
          )}
        </Box>
      )}

      {/* Users Activity Log */}
      <Box sx={{ mt: 4 }}>
        <UsersActivityLog isDarkMode={isDarkMode} />
      </Box>

      {/* View User Dialog */}
      <ViewUserDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        user={selectedUser}
        isDarkMode={isDarkMode}
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        user={selectedUser}
        onUpdateUser={handleUpdateUser}
        isDarkMode={isDarkMode}
      />

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        isDarkMode={isDarkMode}
      />

      {/* Create User Dialog */}
      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreateUser={handleCreateUser}
        isDarkMode={isDarkMode}
      />

      {/* Notification Component */}
      <NotificationComponent />
    </Box>
  );
};

export default UsersPage;
